import {DateTime} from 'luxon'

// -----------------------------------------------------------------------------
//  Local Date Conversions
// -----------------------------------------------------------------------------

/**
 * Converts various date inputs into a Luxon DateTime object in user's local timezone.
 * @param {any} input - The date input (string, Date, or DateTime)
 * @returns {DateTime} A Luxon DateTime object in local timezone
 */
export function toLocalDateTime(input: any) {
  if (!input) {
    throw new TypeError('Unsupported input type for toDateTime()')
  }

  if (DateTime.isDateTime(input)) {
    return input
  }

  if (input instanceof Date) {
    return DateTime.fromJSDate(input)
  }

  if (typeof input === 'string') {
    let dt = DateTime.fromISO(input, {setZone: true})
    if (!dt.isValid) {
      dt = DateTime.fromJSDate(new Date(input))
    }
    return dt
  }

  throw new TypeError('Unsupported input type for toDateTime()')
}

/**
 * Converts a date input to a JavaScript Date object in user's local timezone.
 * @param {any} input - The date input (string, Date, or DateTime)
 * @returns {Date} A JavaScript Date object in local timezone
 */
export function toLocalJSDate(input: any): Date {
  return toLocalDateTime(input).toJSDate()
}

// -----------------------------------------------------------------------------
//  Property Date Conversions
// -----------------------------------------------------------------------------
/**
 * Checks if input is a valid date.
 * @param {any} input - The date input to validate
 * @returns {boolean} True if the input is a valid date, false otherwise
 */
export function isValid(input: any): boolean {
  try {
    return toLocalDateTime(input).isValid
  } catch (e) {
    return false
  }
}

/**
 * Normalize various date inputs into a Luxon DateTime object.
 * @param {any} input - The date input (string, Date, or DateTime)
 * @param {string} [zone] - The property IANA time zone.  Defaults to "America/New_York"
 * @returns {DateTime} A Luxon DateTime object
 */
export function toPropertyDateTime(
  input: any,
  zone: string = 'America/New_York'
) {
  if (!input) {
    throw new TypeError('Unsupported input type for toDateTime()')
  }

  if (DateTime.isDateTime(input)) {
    return zone ? input.setZone(zone) : input
  }

  if (input instanceof Date) {
    return DateTime.fromJSDate(input, {zone})
  }

  if (typeof input === 'string') {
    let dt = DateTime.fromISO(input, {zone})
    if (!dt.isValid) {
      dt = DateTime.fromJSDate(new Date(input), {zone})
    }
    return dt
  }

  throw new TypeError('Unsupported input type for toDateTime()')
}

/**
 * Converts a date input to a JavaScript Date object.
 * @param {any} input - The date input (string, Date, or DateTime)
 * @param {string} zone - The property IANA time zone (e.g., "America/New_York")
 * @returns {Date} A JavaScript Date object
 */
export function toPropertyJSDate(
  input: any,
  zone: string = 'America/New_York'
): Date {
  return toPropertyDateTime(input, zone).toJSDate()
}

// -----------------------------------------------------------------------------
//  Date formatters
// -----------------------------------------------------------------------------

/**
 * Formats an ISO date.  ex. 2024-12-25
 * @param {DateTime} dt - The date input (string, Date, or DateTime)
 * @returns {string} A date in yyyy-MM-dd format
 */
export function formatISODate(dt: DateTime): string {
  return dt.toISODate() as string
}

/**
 * Formats a full ISO date.  ex. 2025-11-17T15:00:00-05:00
 * @param {DateTime} dt - The date input (string, Date, or DateTime)
 * @returns {string} A date in ISO-8601 format
 */
export function formatISODateTime(dt: DateTime): string {
  return dt.toISO() as string
}

/**
 * Formats a display date.  ex. Dec 25, 2024
 * @param {DateTime} dt - The date input (string, Date, or DateTime)
 * @returns {string} A date in MMM d, yyyy format
 */
export function formatDisplayDate(dt: DateTime): string {
  return dt.toFormat('MMM d, yyyy')
}

/**
 * Adds days to the input date and returns the new date.
 * @param {any} input - The date input (string, Date, or DateTime)
 * @param {number} n - Number of days to add
 * @returns {DateTime} A new DateTime with the added days
 */
export function addDays(input: any, n: number): DateTime {
  const dt = toLocalDateTime(input)
  return dt.plus({days: n})
}

/**
 * Subtracts days from the input date and returns the new date.
 * @param {any} input - The date input (string, Date, or DateTime)
 * @param {number} n - Number of days to subtract
 * @returns {DateTime} A new DateTime with the subtracted days
 */
export function subtractDays(input: any, n: number): DateTime {
  const dt = toLocalDateTime(input)
  return dt.minus({days: n})
}

/**
 * Calculates the difference in days between two dates.
 * @param {any} input1 - The first date input (string, Date, or DateTime)
 * @param {any} input2 - The second date input (string, Date, or DateTime)
 * @returns {number} The difference in days
 */
export function differenceInDays(input1: any, input2: any): number {
  const dt1 = toLocalDateTime(input1)
  const dt2 = toLocalDateTime(input2)
  return dt1.diff(dt2, 'days').days
}

/**
 * Checks if two dates are on the same day.
 * @param {any} input1 - The first date input (string, Date, or DateTime)
 * @param {any} input2 - The second date input (string, Date, or DateTime)
 * @returns {boolean} True if the dates are on the same day
 */
export function isSameDay(input1: any, input2: any): boolean {
  const dt1 = toLocalDateTime(input1)
  const dt2 = toLocalDateTime(input2)
  return dt1.hasSame(dt2, 'day')
}

/**
 * Checks if the first date is before the second date (day comparison).
 * @param {any} input1 - The first date input (string, Date, or DateTime)
 * @param {any} input2 - The second date input (string, Date, or DateTime)
 * @returns {boolean} True if the first date is before the second date
 */
export function isBeforeDay(input1: any, input2: any): boolean {
  const dt1 = toLocalDateTime(input1)
  const dt2 = toLocalDateTime(input2)
  const dt1Start = dt1.startOf('day')
  const dt2Start = dt2.startOf('day')
  return dt1Start < dt2Start
}

/**
 * Checks if the first date is after the second date (day comparison).
 * @param {any} input1 - The first date input (string, Date, or DateTime)
 * @param {any} input2 - The second date input (string, Date, or DateTime)
 * @returns {boolean} True if the first date is after the second date
 */
export function isAfterDay(input1: any, input2: any): boolean {
  const dt1 = toLocalDateTime(input1)
  const dt2 = toLocalDateTime(input2)
  const dt1Start = dt1.startOf('day')
  const dt2Start = dt2.startOf('day')
  return dt1Start > dt2Start
}

/**
 * Checks if the first date is after the second date (exact time comparison).
 * @param {any} input1 - The first date input (string, Date, or DateTime)
 * @param {any} input2 - The second date input (string, Date, or DateTime)
 * @returns {boolean} True if the first date is after the second date
 */
export function isAfter(input1: any, input2: any): boolean {
  const dt1 = toLocalDateTime(input1)
  const dt2 = toLocalDateTime(input2)
  return dt1 > dt2
}
